import { z } from 'zod'

// Provider names
export enum ProviderTypes {
  Cartesia = 'cartesia',
  Deepgram = 'deepgram',
  Sarvam = 'sarvam',
  Nimble = 'nimble',
  ElevenLabs = 'elevenlabs',
}

// Model names
export enum ModelTypes {
  Cartesia = 'sonic-2', // TTS model
  CartesiaSTT = 'ink-whisper', // STT model,
  Deepgram = 'nova-2',
  DeepgramNova3 = 'nova-3',
  Sarvam = 'saaras:v2.5',
  ElevenLabs = 'eleven_turbo_v2_5',
}

// ElevenLabs model variants
export enum ElevenLabsModels {
  TurboV2_5 = 'eleven_turbo_v2_5',
  FlashV2_5 = 'eleven_flash_v2_5',
}

// Sarvam bulbul TTS model
export enum SarvamModels {
  BulbulV3 = 'bulbul:v3',
}

const pronunciationSchema = z.object({
  word: z.string().optional(),
  guide: z.string().optional(),
})

export const textToSpeechSchema = z.object({
  provider: z.string().optional(),
  // Accepts any provider model id (ElevenLabs eleven_*, Sarvam bulbul:v3, Cartesia sonic-2,
  // custom-voice models); the provider decides which value is written.
  model: z.string().default(ElevenLabsModels.TurboV2_5),
  voice: z.string().optional(),
  voice_label: z.string().optional(),
  // Union of every provider's speed/pace range (ElevenLabs 0.7-1.2, Sarvam 0.5-2.0);
  // the UI clamps to the selected provider's range.
  voice_speed: z.number().min(0.5).max(2.0).optional(),
  pronunciation: z.array(pronunciationSchema).optional(),
})

export const userIdleConfigSchema = z.object({
  reminder_msg: z.string().optional(),
  hangup_msg: z.string().optional(),
  idling_timeout: z.number().min(0).max(100).default(15),
  hold_timeout: z.number().min(0).max(100).default(30),
  retries: z.number().min(0).max(10),
})

export const speechToTextSchema = z.object({
  provider: z.string().optional(),
  model: z.string().optional(),
  keyterms: z.array(z.string()).optional(),
})

export const backgroundAudioSchema = z.object({
  type: z.string().default('none'),
  volume: z.number().min(0).max(2).default(1),
  thinking_sound_enabled: z.boolean().default(true),
  thinking_sound_volume: z.number().min(0).max(1).default(0.7),
})

export const voiceAiConfigSchema = z.object({
  language: z.string().min(1, { message: 'At least one language is required' }),
  text_to_speech: textToSpeechSchema.default({}),
  speech_to_text: speechToTextSchema.optional(),
  user_idle_config: userIdleConfigSchema,
  allow_greeting_interruption: z.boolean().optional(),
  background_audio: backgroundAudioSchema.optional(),
})
export enum AuthType {
  NO_AUTH = 'no_auth',
  BASIC_AUTH = 'basic_auth',
  BEARER_TOKEN = 'bearer_token',
}
export const eventCallbackBase = z.object({
  url: z
    .string()
    .refine(
      (val) => {
        // If empty, it's valid (optional)
        if (!val) return true
        // Check if it's a valid URL using URL.canParse
        return URL.canParse(val)
      },
      { message: 'Valid URL required' },
    )
    .default(''),
  method: z.enum(['POST', 'GET']).default('POST'),
  auth: z
    .enum([AuthType.NO_AUTH, AuthType.BASIC_AUTH, AuthType.BEARER_TOKEN])
    .default(AuthType.NO_AUTH),
  username: z.string().default(''),
  password: z.string().default(''),
  token: z.string().default(''),
})

const hangupCallbackSchema = eventCallbackBase.extend({
  type: z.enum(['url', 'agent']).default('url'),
})
const recordingCallbackSchema = eventCallbackBase.extend({
  transcription: z.boolean().default(false),
})

export const eventCallbacksSchema = z.object({
  HANGUP: hangupCallbackSchema,
  RECORDING: recordingCallbackSchema,
  ERROR: eventCallbackBase,
  SMS_STATUS: eventCallbackBase,
  WHATSAPP_STATUS: eventCallbackBase,
  CONVERSATION_CLOSURE: eventCallbackBase,
})

// Helper function to get default values from schema
export const getEventCallbackDefaults = () => {
  return {
    HANGUP: hangupCallbackSchema.parse({}),
    RECORDING: recordingCallbackSchema.parse({}),
    ERROR: eventCallbackBase.parse({}),
    SMS_STATUS: eventCallbackBase.parse({}),
    WHATSAPP_STATUS: eventCallbackBase.parse({}),
    CONVERSATION_CLOSURE: eventCallbackBase.parse({}),
  }
}

export const conversationGoalSchema = z.object({
  id: z.number().optional(),
  uuid: z.string().optional(),
  goal_name: z.string(),
  goal_instructions: z.string(),
})

export const agentSettingsSchema = z.object({
  schedule: z
    .object({
      timezone: z.string().optional(),
      start_time: z.string().optional(),
      end_time: z.string().optional(),
    })
    .nullish(),
  knowledge_base_ids: z.array(z.string()),
  // The subaccount tag rides agentSettings like voice_ai_config/schedule;
  // null/absent = untagged. Persisted through the normal flow save (config-service
  // validates a change server-side: parent-ownership + AI-only fence).
  sub_auth_id: z.string().nullish(),
  voice_ai_config: voiceAiConfigSchema.optional(),
  event_callbacks: eventCallbacksSchema.optional(),
  conversation_goals: z
    .array(conversationGoalSchema)
    .max(10, { message: 'Maximum of 10 goals allowed' })
    .optional()
    .refine(
      (goals) => {
        if (!goals) return true
        const names = goals.map((g) => g.goal_name.toLowerCase().trim()).filter(Boolean)
        return names.length === new Set(names).size
      },
      { message: 'Duplicate goal names are not allowed' },
    ),
})

// Mapping function to convert language codes to Cartesia voice accents
export const getVoiceAccentForLanguage = (languageCode: string): string[] => {
  const mapping: Record<string, string[]> = {
    en: ['en-US'], // English (US) -> US voices
    ar: [], // Arabic -> no voices available yet
  }

  return mapping[languageCode] || [languageCode]
}
